import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useViewMode } from '../../contexts/ViewModeContext';

/**
 * Slim banner shown when the displayed shell (viewMode) differs from the
 * account's natural experience (accountMode). It nudges the user back to the
 * view their account actually supports -- real feature access stays gated by
 * is_recruiter regardless.
 */
const ViewModeNudge: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { viewMode, accountMode, isMismatch, setViewMode } = useViewMode();
  const [dismissed, setDismissed] = useState(false);

  // Re-show whenever the mismatch direction changes.
  useEffect(() => {
    setDismissed(false);
  }, [viewMode]);

  if (!isMismatch || !accountMode || dismissed) return null;

  const message = viewMode === 'recruiter'
    ? t('viewModeNudge.viewingRecruiter', "You're viewing the recruiter side. Hiring needs a recruiter account.")
    : t('viewModeNudge.viewingExplorer', "You're viewing the explorer side. Exploring needs an explorer account.");

  const ctaLabel = accountMode === 'recruiter'
    ? t('viewModeNudge.backToRecruiter', 'Back to recruiter view')
    : t('viewModeNudge.backToExplorer', 'Back to explorer view');

  const handleSwitchBack = () => {
    setViewMode(accountMode);
    navigate(accountMode === 'recruiter' ? '/?tab=hire' : '/?tab=explore');
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-900">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-1.5 flex items-center gap-2 sm:gap-3">
        <p className="text-[11px] sm:text-xs leading-snug flex-1 min-w-0">{message}</p>
        <button
          onClick={handleSwitchBack}
          className="flex-shrink-0 text-[11px] sm:text-xs font-semibold text-pink-600 hover:text-pink-700 underline underline-offset-2 whitespace-nowrap"
        >
          {ctaLabel}
        </button>
        <button
          onClick={() => setDismissed(true)}
          aria-label={t('common.dismiss', 'Dismiss')}
          className="flex-shrink-0 text-amber-500 hover:text-amber-700 transition-colors"
        >
          <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </button>
      </div>
    </div>
  );
};

export default ViewModeNudge;
